// Why an applicant's invite link can't be used. The API answers these on any applicant call:
//   401 { code: 'link_invalid' }        unknown / bad token
//   410 { code: 'link_expired' }        more than 30 days since the last invite
//   410 { code: 'application_closed' }
// The verification container listens and swaps the flow for a dedicated screen, so a dead link
// says so instead of showing an empty form with Continue disabled.
export type LinkProblem = 'link_invalid' | 'link_expired' | 'application_closed';

/**
 * Pure: the link problem an applicant-call failure means, or null for any other failure (which
 * the caller handles). Every 401 on an applicant endpoint means the token was refused, and every
 * 410 means the link is gone, so neither depends on the code being present.
 */
export const linkProblemFromResponse = (status: number | undefined, data: unknown): LinkProblem | null => {
  const code = data && typeof data === 'object' ? (data as { code?: unknown }).code : undefined;
  if (status === 401) return 'link_invalid';
  if (status === 410) return code === 'application_closed' ? 'application_closed' : 'link_expired';
  return null;
};

type Listener = (problem: LinkProblem) => void;
const listeners = new Set<Listener>();

export const reportLinkProblem = (problem: LinkProblem): void => {
  listeners.forEach(listener => listener(problem));
};

/** Subscribes; returns the unsubscribe function (so it can be returned from a useEffect). */
export const onLinkProblem = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
